import React from 'react';
import { Link } from 'react-router-dom';
import GuideLayout, { InfoBox, SectionCTA } from '../../components/GuideLayout';

const s = {
  breadcrumb: { fontSize: 13, color: '#94a3b8', marginBottom: 20 },
  breadcrumbLink: { color: '#2563eb', textDecoration: 'none' },
  h1: { fontSize: 32, fontWeight: 800, color: '#0f172a', lineHeight: 1.25, marginBottom: 16, letterSpacing: '-0.4px' },
  lead: { fontSize: 17, color: '#475569', lineHeight: 1.7, marginBottom: 32, borderBottom: '1px solid #e2e8f0', paddingBottom: 32 },
  h2: { fontSize: 22, fontWeight: 700, color: '#0f172a', marginTop: 36, marginBottom: 14, letterSpacing: '-0.2px' },
  h3: { fontSize: 17, fontWeight: 600, color: '#1e293b', marginTop: 24, marginBottom: 10 },
  p: { fontSize: 15, color: '#374151', lineHeight: 1.75, marginBottom: 16 },
  ul: { paddingLeft: 20, marginBottom: 16 },
  li: { fontSize: 15, color: '#374151', lineHeight: 1.75, marginBottom: 6 },
  weightingExample: {
    background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12,
    overflow: 'hidden', marginBottom: 24,
  },
  weightingRow: (isHeader) => ({
    display: 'grid', gridTemplateColumns: '1fr auto',
    padding: '10px 18px',
    background: isHeader ? '#f8fafc' : '#fff',
    borderBottom: '1px solid #f1f5f9',
    fontSize: isHeader ? 12 : 14,
    fontWeight: isHeader ? 700 : 400,
    color: isHeader ? '#64748b' : '#374151',
  }),
  weightBar: (pct, color) => ({
    height: 8, borderRadius: 4, marginTop: 4,
    width: pct + '%', background: color, minWidth: 4,
  }),
  mistakeList: { marginBottom: 24 },
  mistake: {
    background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10,
    padding: '14px 18px', marginBottom: 8,
    display: 'flex', gap: 14, alignItems: 'flex-start',
  },
  mistakeIcon: { fontSize: 16, flexShrink: 0, marginTop: 1 },
  mistakeText: { fontSize: 14, color: '#374151', lineHeight: 1.6 },
  nav: { display: 'flex', justifyContent: 'space-between', marginTop: 48, paddingTop: 24, borderTop: '1px solid #e2e8f0' },
  prevLink: { fontSize: 14, color: '#2563eb', textDecoration: 'none', fontWeight: 600 },
  nextLink: { fontSize: 14, color: '#2563eb', textDecoration: 'none', fontWeight: 600 },
};

const weightingRows = [
  { criterion: 'Technical methodology', weight: '30%', bar: 30, color: '#2563eb' },
  { criterion: 'Relevant experience and case studies', weight: '25%', bar: 25, color: '#2563eb' },
  { criterion: 'Price / commercial proposal', weight: '30%', bar: 30, color: '#059669' },
  { criterion: 'Team CVs and capability', weight: '10%', bar: 10, color: '#2563eb' },
  { criterion: 'Social value / sustainability', weight: '5%', bar: 5, color: '#7c3aed' },
];

const mistakes = [
  { icon: '✗', text: 'Not reading the specification carefully enough and missing a sub-criterion entirely — even one missed criterion can drop you from shortlist.' },
  { icon: '✗', text: 'Generic responses that could apply to any contract. Evaluators score evidence, not assertions. "We are experienced in this area" scores zero without a case study to back it up.' },
  { icon: '✗', text: 'Pricing too low. Implausibly cheap bids can be rejected as "abnormally low" and raise questions about your ability to deliver. Price to win, not to undercut.' },
  { icon: '✗', text: 'Submitting at the last minute. Procurement portals can be slow; late submissions are almost always disqualified regardless of reason. Build in a 24-hour buffer.' },
  { icon: '✗', text: 'Forgetting supporting documents. A strong proposal disqualified for a missing insurance certificate is a waste of everyone\'s time.' },
  { icon: '✗', text: 'Not asking clarification questions. The clarification period exists precisely so you can resolve ambiguities. Bidders who ask good questions often demonstrate greater understanding of the requirement.' },
];

export default function WinningABid() {
  return (
    <GuideLayout>
      <div style={s.breadcrumb}>
        <Link to="/guide" style={s.breadcrumbLink}>Tender Guide</Link> &rsaquo; What makes a winning bid?
      </div>

      <h1 style={s.h1}>What makes a winning bid?</h1>
      <p style={s.lead}>
        Winning a public tender is not about having the lowest price or the biggest name — it is about demonstrating, in writing, that you understand the requirement and can deliver it. The evaluation is structured and scored: the buyer cannot deviate from their published criteria.
      </p>

      <h2 style={s.h2}>How bids are scored: MEAT</h2>
      <p style={s.p}>
        EU procurement rules require that contracts be awarded on the basis of the <strong>Most Economically Advantageous Tender (MEAT)</strong>. This means buyers must evaluate bids on a combination of quality and price — not on price alone.
      </p>
      <p style={s.p}>
        The weighting between quality and price must be published in the contract notice. A typical professional services contract might look like this:
      </p>

      <div style={s.weightingExample}>
        <div style={s.weightingRow(true)}>
          <span>Evaluation criterion</span>
          <span>Weight</span>
        </div>
        {weightingRows.map((r, i) => (
          <div key={i} style={s.weightingRow(false)}>
            <div>
              <div>{r.criterion}</div>
              <div style={s.weightBar(r.bar, r.color)} />
            </div>
            <span style={{ fontWeight: 600, color: '#1e293b' }}>{r.weight}</span>
          </div>
        ))}
      </div>

      <InfoBox>
        <strong>Important:</strong> The weighting varies significantly by contract type. IT infrastructure contracts often weight price at 40&ndash;60%. Social care and advisory contracts frequently weight quality at 70%+ because the risk of a poor-quality provider is high. Always check the specific weights in the ITT before deciding whether to bid.
      </InfoBox>

      <h2 style={s.h2}>What evaluators actually look for</h2>
      <p style={s.p}>Evaluation panels are often made up of non-technical procurement officers alongside subject matter experts. They are working from a scoring guide tied to specific sub-criteria. What they reward:</p>

      <h3 style={s.h3}>Evidence over assertion</h3>
      <p style={s.p}>
        Every quality statement should be supported by a specific example. "We have delivered many similar projects" scores poorly. "In 2023 we delivered a GDPR compliance review for [public authority], resulting in X outcome, under budget" scores well. Use the STAR format: Situation, Task, Action, Result.
      </p>

      <h3 style={s.h3}>Understanding the specific requirement</h3>
      <p style={s.p}>
        Strong bids reflect the buyer's language and priorities back at them. If the specification mentions three times that local presence matters, your response should address local presence three times. Mirror the structure of the specification in your response so evaluators can find your answers easily.
      </p>

      <h3 style={s.h3}>A credible delivery approach</h3>
      <p style={s.p}>
        Walk the buyer through exactly how you would deliver the contract — stages, milestones, who does what, how you handle problems. Evaluators give higher scores to bids that demonstrate the bidder has thought through delivery challenges, not just described a generic methodology.
      </p>

      <h3 style={s.h3}>Named, committed team members</h3>
      <p style={s.p}>
        Many contracts require you to name key personnel. A named project lead with a strong CV scores better than "a suitably experienced consultant." Buyers are often nervous about bait-and-switch (winning with a senior team, delivering with juniors) — address this directly.
      </p>

      <h2 style={s.h2}>Six common mistakes that cost companies contracts</h2>
      <div style={s.mistakeList}>
        {mistakes.map((m, i) => (
          <div key={i} style={s.mistake}>
            <span style={{ ...s.mistakeIcon, color: '#b91c1c' }}>{m.icon}</span>
            <span style={s.mistakeText}>{m.text}</span>
          </div>
        ))}
      </div>

      <h2 style={s.h2}>Bid / no-bid: when to walk away</h2>
      <p style={s.p}>
        Not every published tender is worth responding to. A realistic bid win rate for a well-run organisation bidding selectively is 30&ndash;40%. Bidding indiscriminately produces win rates closer to 5%. Before committing to a bid, ask:
      </p>
      <ul style={s.ul}>
        <li style={s.li}>Do we genuinely meet the qualification criteria, or will we be stretching?</li>
        <li style={s.li}>Do we have two or three strong case studies that map closely to this requirement?</li>
        <li style={s.li}>Do we know anything about this buyer's priorities beyond what is in the notice?</li>
        <li style={s.li}>Is the contract value and margin worth the bid cost (typically 2&ndash;5% of contract value in staff time)?</li>
        <li style={s.li}>Are there any signals the contract is effectively pre-wired for an incumbent?</li>
      </ul>
      <p style={s.p}>
        Saying no to the wrong tenders is as strategically important as winning the right ones.
      </p>

      <SectionCTA />

      <div style={s.nav}>
        <Link to="/guide/bidding-process" style={s.prevLink}>&larr; The bidding process</Link>
        <Link to="/guide/eu-rules" style={s.nextLink}>Next: EU procurement rules &rarr;</Link>
      </div>
    </GuideLayout>
  );
}
